import {
  PRESS_PHOTO_VARIANTS,
  profileImageSpec,
  type ArtistProfileKind,
  type ArtistProfileResponse,
  type SerializedProfileAsset,
} from 'fym-shared/src/music24/artistProfile';
import { profileSlotChip, slotsChip, spotifyCardChip, type ProfileChip } from '../profileChips';
import { profileIsEmpty, profileSlotOf, profileSlotsOf, type ProfileSlot } from '../profileSlots';
import { retryItemOf } from '../profileAssets';
import { spotifyButtonMode } from '../spotify/spotifyUploadState';

/**
 * The model of the Studio "Your profile" card (design profile-card a, frames p1–p7): five tiles
 * (Spotify, YouTube banner, Facebook cover, press photos, bio), each with its frames at the true
 * aspect of its file, a chip and one quick action. Pure: the components only draw it.
 */

export type ProfileCardTileKey = 'spotify' | 'youtube' | 'facebook' | 'press' | 'bio';

/** What one frame of a tile shows. */
export type ProfileCardFrameContent =
  | { type: 'file'; asset: SerializedProfileAsset }
  /** Nothing made: the blurred cover of the newest release, so the artist sees what comes (p3). */
  | { type: 'ghost' }
  | { type: 'placeholder' }
  | { type: 'making' }
  | { type: 'bio'; text: string }
  | { type: 'bioEmpty' };

export interface ProfileCardFrame {
  /** Width over height of the file. */
  aspect: number;
  round: boolean;
  content: ProfileCardFrameContent;
}

/** The quick action of a tile (design p1 note "Quick action"). */
export type ProfileCardAction =
  | { kind: 'sendSpotify' }
  | { kind: 'connectSpotify' }
  | { kind: 'reconnectSpotify' }
  | { kind: 'upgradeSpotify' }
  | { kind: 'iSetIt'; assetUuid: string }
  | { kind: 'make'; profileKind: ArtistProfileKind; catalogItemUuid: string }
  | { kind: 'retry'; asset: SerializedProfileAsset }
  | { kind: 'openKind'; profileKind: ArtistProfileKind; reason: 'retry' | 'make' }
  | { kind: 'addPhoto' }
  | { kind: 'download'; asset: SerializedProfileAsset }
  | { kind: 'copyShort'; text: string }
  | { kind: 'writeBio' };

/** The chip of the bio: a text of the card, not an image state. */
export type ProfileCardChip = ProfileChip | { key: 'bioSaved' | 'bioEmpty' | 'bioOnSpotify'; tone: 'ok' | 'nil' };

export interface ProfileCardTile {
  key: ProfileCardTileKey;
  frames: ProfileCardFrame[];
  chip: ProfileCardChip;
  action: ProfileCardAction | null;
  /** True while a file of the tile is made: the frame shines amber. */
  making: boolean;
}

/** The state of the whole card (design p1–p4; loading p5 has no model). */
export type ProfileCardMode = 'noSong' | 'empty' | 'some' | 'making' | 'done';

export interface ProfileCardModel {
  mode: ProfileCardMode;
  /** The ready kinds of the five (the press photo counts once; the bio does not count). */
  ready: number;
  total: number;
  /** The image kinds with no file yet, in the order of the card (the sub line names them). */
  notMade: ArtistProfileKind[];
  /** The image kinds that are being made now. */
  making: ArtistProfileKind[];
  tiles: ProfileCardTile[];
}

const ASPECT = (kind: ArtistProfileKind, variant?: (typeof PRESS_PHOTO_VARIANTS)[number]): number => {
  const spec = profileImageSpec(kind, variant);
  return spec.width / spec.height;
};

/** The frame of one image slot: the file, else the old file while the new one is made, else a stand-in. */
function slotFrame(slot: ProfileSlot, empty: boolean, round = false): ProfileCardFrame {
  const aspect = ASPECT(slot.kind, slot.variant === 'main' ? undefined : slot.variant);
  if (slot.current !== null && !slot.current.fileRemoved) {
    return { aspect, round, content: { type: 'file', asset: slot.current } };
  }
  if (slot.state === 'making') {
    return { aspect, round, content: { type: 'making' } };
  }
  const ghost = empty && profileImageSpec(slot.kind).coverMode === 'blur';
  return { aspect, round, content: ghost ? { type: 'ghost' } : { type: 'placeholder' } };
}

type Profile = Pick<ArtistProfileResponse, 'assets' | 'bio' | 'newestRelease' | 'preview' | 'spotify'>;

/** "Try again" with the same make, or the tool when the make cannot be repeated (the picture is gone). */
function retryOrOpen(asset: SerializedProfileAsset): ProfileCardAction {
  return retryItemOf(asset) === null
    ? { kind: 'openKind', profileKind: asset.kind, reason: 'retry' }
    : { kind: 'retry', asset };
}

function spotifyAction(profile: Profile, header: ProfileSlot, avatar: ProfileSlot): ProfileCardAction | null {
  if (header.failed !== null) {
    return retryOrOpen(header.failed);
  }
  if (avatar.failed !== null) {
    return retryOrOpen(avatar.failed);
  }
  const ready = [header.current, avatar.current].filter((one): one is SerializedProfileAsset => one !== null);
  if (ready.length === 0) {
    return null;
  }
  // Every ready file is on Spotify already: nothing to do ("Changed on Spotify" brings Send back).
  if (ready.every((one) => one.spotify?.state === 'onSpotify' || one.spotify?.state === 'sending')) {
    return null;
  }
  const mode = spotifyButtonMode(profile);
  if (mode === 'manual') {
    return null;
  }
  if (mode === 'upgrade') {
    return { kind: 'upgradeSpotify' };
  }
  if (profile.spotify.unavailable === 'notConnected') {
    return { kind: 'connectSpotify' };
  }
  if (profile.spotify.unavailable === 'reconnect') {
    return { kind: 'reconnectSpotify' };
  }
  return { kind: 'sendSpotify' };
}

function bannerAction(slot: ProfileSlot, profile: Profile, empty: boolean): ProfileCardAction | null {
  if (slot.state === 'making') {
    return null;
  }
  if (slot.failed !== null) {
    return retryOrOpen(slot.failed);
  }
  if (slot.current === null) {
    // The empty card makes the set with one button; each tile has no own action there (p3).
    if (empty && profile.newestRelease !== null) {
      return null;
    }
    return profile.newestRelease === null
      ? { kind: 'openKind', profileKind: slot.kind, reason: 'make' }
      : { kind: 'make', profileKind: slot.kind, catalogItemUuid: profile.newestRelease.catalogItemUuid };
  }
  return slot.current.setAt === null ? { kind: 'iSetIt', assetUuid: slot.current.uuid } : null;
}

function pressAction(slots: ProfileSlot[]): ProfileCardAction | null {
  if (slots.some((slot) => slot.state === 'making')) {
    return null;
  }
  const landscape = slots[0];
  if (landscape?.current !== null && landscape?.current !== undefined && !landscape.current.fileRemoved) {
    return { kind: 'download', asset: landscape.current };
  }
  const failed = slots.find((slot) => slot.failed !== null)?.failed ?? null;
  return failed !== null ? { kind: 'openKind', profileKind: 'pressPhoto', reason: 'retry' } : { kind: 'addPhoto' };
}

function bioTile(profile: Profile): ProfileCardTile {
  const short = profile.bio.bioShort.trim();
  const long = profile.bio.bioLong.trim();
  const shown = short !== '' ? short : long;
  if (shown === '') {
    return {
      key: 'bio',
      frames: [{ aspect: 1.9, round: false, content: { type: 'bioEmpty' } }],
      chip: { key: 'bioEmpty', tone: 'nil' },
      action: { kind: 'writeBio' },
      making: false,
    };
  }
  return {
    key: 'bio',
    frames: [{ aspect: 1.9, round: false, content: { type: 'bio', text: shown } }],
    chip: profile.spotify.bio.sentIsCurrent ? { key: 'bioOnSpotify', tone: 'ok' } : { key: 'bioSaved', tone: 'ok' },
    action: short !== '' ? { kind: 'copyShort', text: short } : { kind: 'writeBio' },
    making: false,
  };
}

/** The kinds that the card counts, in its order. */
const COUNTED: readonly ArtistProfileKind[] = [
  'spotifyHeader',
  'spotifyAvatar',
  'youtubeBanner',
  'facebookCover',
  'pressPhoto',
];

function kindReady(profile: Profile, kind: ArtistProfileKind): boolean {
  return profile.assets.some((one) => one.kind === kind && one.current?.status === 'ready');
}

function kindMaking(profile: Profile, kind: ArtistProfileKind): boolean {
  return profile.assets.some(
    (one) => one.kind === kind && (one.newest?.status === 'queued' || one.newest?.status === 'rendering')
  );
}

/** The model of the card from the profile answer. Pure. */
export function profileCardModel(profile: Profile): ProfileCardModel {
  const empty = profileIsEmpty(profile.assets);
  const header = profileSlotOf(profile.assets, 'spotifyHeader');
  const avatar = profileSlotOf(profile.assets, 'spotifyAvatar');
  const youtube = profileSlotOf(profile.assets, 'youtubeBanner');
  const facebook = profileSlotOf(profile.assets, 'facebookCover');
  const press = profileSlotsOf(profile.assets, 'pressPhoto', PRESS_PHOTO_VARIANTS);
  const banner = (key: 'youtube' | 'facebook', slot: ProfileSlot): ProfileCardTile => ({
    key,
    frames: [slotFrame(slot, empty)],
    chip: profileSlotChip(slot),
    action: bannerAction(slot, profile, empty),
    making: slot.state === 'making',
  });
  const tiles: ProfileCardTile[] = [
    {
      key: 'spotify',
      frames: [slotFrame(header, empty), slotFrame(avatar, false, true)],
      chip: spotifyCardChip(header, avatar),
      action: spotifyAction(profile, header, avatar),
      making: header.state === 'making' || avatar.state === 'making',
    },
    banner('youtube', youtube),
    banner('facebook', facebook),
    {
      key: 'press',
      frames: [slotFrame(press[0] ?? profileSlotOf(profile.assets, 'pressPhoto', 'landscape'), false)],
      chip: slotsChip(press),
      action: pressAction(press),
      making: press.some((slot) => slot.state === 'making'),
    },
    bioTile(profile),
  ];
  const ready = COUNTED.filter((kind) => kindReady(profile, kind)).length;
  const making = COUNTED.filter((kind) => kindMaking(profile, kind));
  const notMade = COUNTED.filter((kind) => !kindReady(profile, kind) && !kindMaking(profile, kind));
  let mode: ProfileCardMode = 'some';
  if (empty && making.length === 0) {
    mode = profile.newestRelease === null ? 'noSong' : 'empty';
  } else if (making.length > 0) {
    mode = 'making';
  } else if (ready === COUNTED.length) {
    mode = 'done';
  }
  return { mode, ready, total: COUNTED.length, notMade, making, tiles };
}
